import { COORDINATE_STOCKHOLM } from '../../constants';
import { HOURS_PER_YEAR } from '../../constants/math.constants';
import { Coordinate } from '@shared/models/geography.model';
import { RequireProperties } from '../../models/type-helpers.model';
import { AHU_DEFAULT_HEAT_RECOVERY_EFFICIENCY } from '../../templates/categories/air-handling-unit/air-handling-unit.model';
import { convert } from '../conversion.helpers';
import { toEnergyActivity } from './energy-activity-type.helpers';
import {
  CP_AIR,
  DEFAULT_INDOOR_TEMP,
  AIR_DENSITY,
  IHeatingEnergyCalculationOptions,
  ABSOLUTE_ZERO_TEMP,
  ActivityInput,
} from './energy.models';
import { getHeatingHours } from './heating-energy.helpers';
import { getDegreeHoursFromCoordinate } from './temperature.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';

/**
 * Options for calculating the ventilation energy.
 */
export type IVentilationEnergyOptions = {
  /**
   * The specific fan power of the ventilation system in kW/(m³/s).
   * See: https://en.wikipedia.org/wiki/Specific_fan_power
   */
  specificFanPower?: number;

  /**
   * The efficiency of the heat recovery system in the ventilation system. A number between 0 and 1.
   * See: https://en.wikipedia.org/wiki/Heat_recovery_ventilation
   */
  heatRecoveryEfficiency?: number; // 0-1

  /**
   * The number of hours the ventilation system is operational in a year (hours/year).
   */
  operationHours?: number;

  /**
   * Target temperature after post heating in °C. If not provided post heating is not calculated.
   * Post heating in a ventilation system is the process of heating the air to a desired temperature after it has passed through the heat exchanger.
   * This is often necessary because the recovered heat from the exhaust air via the heat exchanger might not be sufficient to reach the desired supply air temperature, especially during cold periods.
   * Process flow: Outside Air (cold) → Pre Cooling (final temp)  → Heat Recovery (preheated) → Post Heating (final temp) → Supply Air (to rooms)
   *
   */
  postHeatingTemp?: number;

  /**
   * The number of hours the post heating is operational in a year (hours/year).
   */
  postHeatingHours?: number;

  /**
   * Target temperature after pre cooling in °C. If not provided pre cooling is not calculated.
   * Pre cooling in a ventilation system is the process of cooling the air to a desired temperature before it is supplied to the rooms.
   * This is often necessary because the recovered heat from the exhaust air via the heat exchanger might not be sufficient to reach the desired supply air temperature, especially during cold periods.
   * Process flow: Outside Air (cold) → Pre Cooling (final temp) → Heat Recovery (preheated) → Post Heating (final temp) → Supply Air (to rooms)
   */
  preCoolingTemp?: number;

  /**
   * The airflow in m³/s. Can be calculated from the supply and exhaust air flow per area and the area.
   */
  airflow: number;

  /**
   * The ventilated area in m².
   */
  ventilationArea?: number;

  /**
   * The location of the building.
   */
  location?: Coordinate;

  /**
   * The indoor temperature in °C.
   */
  indoorTemp?: number;
};

/**
 * Calculate the energy needed to heat air in kWh/year.
 * @param airflow - The airflow rate in m³/s
 * @param degreeHours - The degree hours in °Ch
 * @param energyRecoveryEfficiency - The efficiency of the heat recovery system in the ventilation system.
 * A number between 0 and 1 where 0 is no recovery and 1 is 100% recovery.
 * @returns The energy loss due to ventilation in kWh/year
 */
export const calculateAirflowEnergy = (
  airflow: number,
  degreeHours: number,
  energyRecoveryEfficiency: number = 0,
): number => {
  // Calculate heating power needed in kW/K
  const heatingPower = (airflow * AIR_DENSITY * CP_AIR) / 1000;

  return heatingPower * degreeHours * (1 - energyRecoveryEfficiency);
};

/**
 * Calculate the energy needed to heat air in W per degree temperature difference.
 * @param airflow - The airflow rate in m³/s
 * @param heatRecoveryEfficiency - The efficiency of the heat recovery system in the ventilation system.
 * A number between 0 and 1 where 0 is no recovery and 1 is 100% recovery.
 * @returns The energy loss due to ventilation in W/°C
 */
export const getAirflowHeatLoss_WPerDegree = (
  airflowInM3PerS: number,
  heatRecoveryEfficiency: number = 0,
): number => {
  return airflowInM3PerS * AIR_DENSITY * CP_AIR * (1 - heatRecoveryEfficiency);
};

/**
 * Calculate the airflow in m³/s based on the supply and exhaust air flow per area and the area.
 * @param supplyAirFlowPerArea - The supply air flow per area in l/m²s
 * @param exhaustAirFlowPerArea - The exhaust air flow per area in l/m²s
 * @param area - The area in m²
 * @returns The airflow in m³/s
 */
export const calculateAirflow = (
  supplyAirFlowPerArea: number,
  exhaustAirFlowPerArea: number,
  area: number,
) => {
  return convert(
    Math.max(supplyAirFlowPerArea, exhaustAirFlowPerArea, 0) * area,
    'l',
    'm³',
  );
};

/**
 * Calculate the ventilation fan energy consumption in kWh/year.
 * @param airflow - The airflow rate in m³/s
 * @param specificFanPower - The specific fan power in kW/(m³/s)
 * @param operationHours - The number of hours the ventilation system is operational in a year (hours/year)
 * @returns The ventilation fan energy consumption in kWh/year
 */
export const calculateVentilationFanEnergyConsumption = (
  airflow: number,
  specificFanPower: number,
  operationHours: number = HOURS_PER_YEAR,
) => {
  return airflow * specificFanPower * operationHours;
};

export const effectRatioToEnergyRecoveryEfficiency = (effectRatio?: number) => {
  if (!effectRatio) {
    return 0;
  }
  return 1 - 1 / effectRatio;
};

interface SeasonalTempDiffParams {
  /**
   * The target temperature after post heating or pre cooling in °C.
   */
  targetTemp?: number;

  /**
   * The average outdoor temperature in °C during the season.
   */
  outdoorTemp: number;

  /*
   * The number of hours the temperature requires ventilation in a year.
   * In Stockholm you have around 5500 hours with heating (when outdoor temp is below 17°C)
   * and 1000 hours with cooling (when outdoor temp is above 22°C) each year.
   */
  seasonHours?: number;

  /**
   * The number of hours the ventilation is operational in a year.
   */
  operationHours?: number;

  /**
   * The efficiency of the heat recovery system in the ventilation system. A number between 0 and 1.
   * See: https://en.wikipedia.org/wiki/Heat_recovery_ventilation
   */
  heatRecoveryEfficiency?: number;
}

/**
 * Calculate how much energy extra energy post heating is causing in kWh/year.
 * @param param0
 * @returns
 */
export const calculatePostHeatingEnergy = ({
  airflow,
  postHeatingTemp,
  location = COORDINATE_STOCKHOLM,
  heatRecoveryEfficiency = 0.8,
  indoorTemp = DEFAULT_INDOOR_TEMP,
}: Pick<
  IVentilationEnergyOptions,
  | 'airflow'
  | 'postHeatingTemp'
  | 'location'
  | 'heatRecoveryEfficiency'
  | 'indoorTemp'
>) => {
  if (!postHeatingTemp) {
    return 0;
  }
  const postHeatingLimitTemp = getPostHeatingLimitTemp({
    postHeatingTemp,
    heatRecoveryEfficiency,
    indoorTemp,
  });

  const postHeatingDegreeHours = getDegreeHoursFromCoordinate(
    location,
    postHeatingLimitTemp,
  );

  // If post heating should be warmer than the default indoor temp, we need to increase the degree hours for the heating season
  return calculateAirflowEnergy(
    airflow,
    postHeatingDegreeHours,
    heatRecoveryEfficiency,
  );
};

/**
 * Get temp difference in relation to the ventialtion system operation hours.
 * So if temp diff is 10°C and ventialtion is only active 50% of the time, the temp diff is 5°C.
 * Can be used for heating and cooling.
 * @returns The temp diff in K/°C
 */
export const calculateOperationHoursTempDiff = ({
  targetTemp = DEFAULT_INDOOR_TEMP,
  outdoorTemp,
  seasonHours = 5500, // Stockholm ~230 days/year for heating
  operationHours = HOURS_PER_YEAR,
}: SeasonalTempDiffParams): number => {
  if (operationHours <= 0) {
    return 0;
  }

  // Calculate seasonal fractions
  const heatingSeasonFraction = seasonHours / operationHours;

  // Calculate the temperature difference (always positive)
  return Math.abs(targetTemp - outdoorTemp) * heatingSeasonFraction;
};

type VentilationHeatingDemandParams = IHeatingEnergyCalculationOptions & {
  /**
   * The efficiency of the heat recovery system in the ventilation system. A number between 0 and 1 where 1 is 100% recovery.
   * See: https://en.wikipedia.org/wiki/Heat_recovery_ventilation
   */
  heatRecoveryEfficiency?: number;

  /**
   * How many hours is the ventilation system operational in a year.
   * If not provided, will be calculated from the operation hours and the operation days.
   */
  operationHours?: number;

  /**
   * Provide post heating temp to base heating demand on temp diff AFTER post heating.
   * Provide undefined to disable post heating.
   */
  postHeatingTemp?: number;

  /**
   * If provided, will be used instead of calculating it from the location and post heating temp
   */
  degreeHours?: number;
};

/**
 * Get how many hours we need post heating
 * @param options
 * @returns
 */
export const getPostHeatingHours = (
  options: VentilationHeatingDemandParams,
): number => {
  const { postHeatingTemp, heatRecoveryEfficiency } = options;

  // Zero hours if no post heating or if heat recovery efficiency is 100%
  if (postHeatingTemp === undefined || heatRecoveryEfficiency === 1) {
    return 0;
  }
  // Need to run 24/7 if post heating temp is the same as indoor temp
  if (postHeatingTemp === DEFAULT_INDOOR_TEMP) {
    return HOURS_PER_YEAR;
  }

  // The temperature at which we need post heating to reach the post heating temperature
  const limitTemp = getPostHeatingLimitTemp({
    ...options,
    postHeatingTemp,
  });
  return getHeatingHours({
    ...options,
    outdoorTemp: limitTemp,
  });
};

/**
 * Get the temperature the heat exchanger will output based on the outdoor temperature, indoor temperature and heat recovery efficiency.
 * @param outdoorTemp
 * @param indoorTemp
 * @param heatRecoveryEfficiency
 * @returns
 */
export const getHeatExchangerOutputTemp = (
  outdoorTemp: number,
  indoorTemp: number = DEFAULT_INDOOR_TEMP,
  heatRecoveryEfficiency: number = AHU_DEFAULT_HEAT_RECOVERY_EFFICIENCY,
) => {
  return heatRecoveryEfficiency * (indoorTemp - outdoorTemp) + outdoorTemp;
};

/**
 * Calculate at which outdoor temperature we need post heating to reach the post heating temperature.
 * This is the inverse of {@link getHeatExchangerOutputTemp}.
 * @param outputTemp - The temperature after the heat exchanger in °C
 * @param indoorTemp - The indoor temperature in °C (default: 21°C)
 * @param heatRecoveryEfficiency - The efficiency of the heat recovery system (0-1, default: 0.8)
 * @returns The outdoor temperature in °C
 */
export const getPostHeatingLimitTemp = ({
  postHeatingTemp,
  heatRecoveryEfficiency = AHU_DEFAULT_HEAT_RECOVERY_EFFICIENCY,
  indoorTemp = DEFAULT_INDOOR_TEMP,
}: RequireProperties<
  VentilationHeatingDemandParams,
  'postHeatingTemp'
>): number => {
  // If the FTX is 100% effective we never need (avoid 0 division)
  if (heatRecoveryEfficiency >= 1) {
    return ABSOLUTE_ZERO_TEMP;
  }

  return (
    (postHeatingTemp - heatRecoveryEfficiency * indoorTemp) /
    (1 - heatRecoveryEfficiency)
  );
};

/**
 * Calculate the heating demand based on building airflow in kWh/year.
 * Observe that post heating will reduce the heating demand since it will make the supply air temperature warmer.
 * @param airflow - The airflow rate in m³/s
 */
export const calculateVentilationHeatingDemand = (
  airflow: number,
  {
    location = COORDINATE_STOCKHOLM,
    operationHours = HOURS_PER_YEAR,
    postHeatingTemp,
    heatRecoveryEfficiency = AHU_DEFAULT_HEAT_RECOVERY_EFFICIENCY,
    indoorTemp = DEFAULT_INDOOR_TEMP,
    degreeHours: overrideDegreeHours,
  }: VentilationHeatingDemandParams = {},
): number => {
  const locationDegreeHours =
    overrideDegreeHours ?? getDegreeHoursFromCoordinate(location);

  // Degree hours for the ventilation system
  const degreeHours =
    postHeatingTemp !== undefined
      ? (indoorTemp - postHeatingTemp) * HOURS_PER_YEAR // Post heating will just reduce the room temp by a few degrees
      : locationDegreeHours;

  // Undefined if no post heating and then it goes for DEFAULT_LIMIT_TEMP
  const operationalRatio = operationHours / HOURS_PER_YEAR;

  // How much heat we will lose due to ventilation
  const airflowEnergyLoss = calculateAirflowEnergy(
    airflow,
    degreeHours * operationalRatio,
    heatRecoveryEfficiency, // If post heating is active, we use the heat recovery efficiency
  );

  return airflowEnergyLoss;
};

/**
 * How many hours per year the ventilation is operational.
 * Basically how many hours a year the apartment / office is occupied.
 * See page 4: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 * @param activity
 * @returns number of hours per year the residents body will generate heat in the building
 */
export const getDefaultVentilationOperationHours = (
  activity?: ActivityInput,
) => {
  const activityId = toEnergyActivity(activity);
  const days = getDefaultVentilationOperationDays(activityId);
  // 07:00 - 18:00 for office and school, 24 hours for other activities
  const hoursPerDay =
    activityId === ActivityType.Office || activityId === ActivityType.School
      ? 18 - 7
      : 24;

  return days * hoursPerDay;
};

/**
 * How many days per year the ventilation is operational.
 * Basically how many days a year the apartment / office is occupied.
 * See page 4: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 * @param activity
 * @returns number of days per year the ventilation is operational
 */
export const getDefaultVentilationOperationDays = (
  activity?: ActivityInput,
) => {
  const activityId = toEnergyActivity(activity);
  if (
    activityId === ActivityType.Office ||
    activityId === ActivityType.School
  ) {
    // 5 days per week * 47 weeks per year
    return 5 * 47;
  }
  return 365;
};
